import {
  CHAT_EFFECT,
  CHAT_FLOW,
  CHAT_INTENT,
  CHAT_STEP,
  CUSTOMER_ROUTES,
} from "./chatStates";
import {
  normalizeChatInput,
  parsePositiveAmount,
  parseTopLevelIntent,
  SEND_CURRENCIES,
} from "./intentParser";
import { chatText, getChatLocale } from "../messages";
import { INPUT_TEXT, inputPolicy } from "./inputPolicy";

let messageSequence = 0;

function message(sender, text, extra = {}) {
  messageSequence += 1;

  return {
    id: `chat-${messageSequence}`,
    sender,
    text,
    ...extra,
  };
}

// Functions, not constants, so the labels follow the locale at the time they are shown.
export function homeReplies() {
  return [{ id: "send", label: chatText("chat.reply.send_money"), value: "send money" }];
}

function fundingReplies() {
  return [
    { id: "bank", label: chatText("chat.reply.bank"), value: "bank" },
    { id: "card", label: chatText("chat.reply.card"), value: "card" },
  ];
}

function append(state, ...messages) {
  return {
    ...state,
    messages: [...state.messages, ...messages],
  };
}

function recipientLabel(recipient) {
  return (
    recipient?.nickname ||
    recipient?.full_name ||
    chatText("chat.recipient.fallback_name")
  );
}

function moneyLabel(currency, amount) {
  const numeric = Number(amount);

  if (!Number.isFinite(numeric)) {
    return `${currency || ""} ${amount || ""}`.trim();
  }

  try {
    return new Intl.NumberFormat(getChatLocale() === "fr" ? "fr-FR" : "en-GB", {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency} ${numeric.toFixed(2)}`;
  }
}

function recipientReplies(recipients = []) {
  return [
    ...recipients.slice(0, 8).map((recipient) => ({
      id: `recipient-${recipient.id}`,

      // Customer sees the name, never the database UUID.
      label: recipientLabel(recipient),
      value: recipientLabel(recipient),

      meta: {
        recipientId: recipient.id,
      },
    })),
    {
      id: "new-recipient",
      label: chatText("chat.reply.new_recipient"),
      value: "__new_recipient__",
    },
  ];
}

function findRecipient(recipients = [], input) {
  const normalized = normalizeChatInput(input)
    .trim()
    .toLowerCase();

  if (!normalized) {
    return null;
  }

  const exact = recipients.find((recipient) => {
    return [
      recipient.id,
      recipient.nickname,
      recipient.full_name,
    ]
      .filter(Boolean)
      .map((value) =>
        String(value).trim().toLowerCase()
      )
      .includes(normalized);
  });

  if (exact) {
    return exact;
  }

  const partial = recipients.filter((recipient) =>
    [
      recipient.nickname,
      recipient.full_name,
    ]
      .filter(Boolean)
      .map((value) =>
        String(value).trim().toLowerCase()
      )
      .some((value) => value.includes(normalized))
  );

  return partial.length === 1 ? partial[0] : null;
}

function quoteEffect(context) {
  const receiveCurrency =
    context.recipient?.currency;

  const destinationCountry =
    context.recipient?.country_code;

  if (
    !context.amount ||
    !context.sendCurrency ||
    !receiveCurrency ||
    !destinationCountry
  ) {
    return null;
  }

  return {
    type: CHAT_EFFECT.PREVIEW_QUOTE,
    payload: {
      send_amount: context.amount,
      send_currency: context.sendCurrency,
      receive_currency: receiveCurrency,
      destination_country: destinationCountry,
    },
  };
}

export function createInitialConversation({
  firstName = "there",
} = {}) {
  return {
    flow: CHAT_FLOW.HOME,
    step: CHAT_STEP.IDLE,
    context: {},
    messages: [
      message(
        "bot",
        chatText("chat.welcome", { first_name: firstName }),
        { kind: "welcome", persisted: true }
      ),
    ],
    quickReplies: homeReplies(),
    effects: [],
  };
}

function resetToHome(
  state,
  text = chatText("chat.home.next")
) {
  return {
    ...append(state, message("bot", text)),
    flow: CHAT_FLOW.HOME,
    step: CHAT_STEP.IDLE,
    context: {},
    quickReplies: homeReplies(),
    effects: [],
  };
}

function navigationResponse(state, text, route) {
  return {
    ...append(state, message("bot", text)),
    effects: [
      {
        type: CHAT_EFFECT.NAVIGATE,
        route,
      },
    ],
    quickReplies: homeReplies(),
  };
}

function kycBlockedResponse(state, event) {
  const status =
    event.kycStatus || "not_started";

  let text = chatText("chat.kyc.required");

  let label = chatText("chat.kyc.verify");

  if (status === "pending") {
    text = chatText("chat.kyc.pending");
    label = chatText("chat.kyc.check");
  }

  if (status === "rejected") {
    text = chatText("chat.kyc.rejected");
    label = chatText("chat.kyc.review");
  }

  if (status === "expired") {
    text = chatText("chat.kyc.expired");
    label = chatText("chat.kyc.update");
  }

  return {
    ...append(state, message("bot", text)),
    flow: CHAT_FLOW.HOME,
    step: CHAT_STEP.IDLE,
    context: {},
    quickReplies: [
      {
        id: "verify",
        label,
        value: "__open_kyc__",
      },
      {
        id: "not-now",
        label: chatText("chat.reply.not_now"),
        value: "home",
      },
    ],
    effects: [],
  };
}

function startSendFlow(state, event, parsed) {
  if (!event.isKycApproved) {
    return kycBlockedResponse(state, event);
  }

  const recipients =
    event.recipients ?? [];

  const recipient = parsed.recipientQuery
    ? findRecipient(
        recipients,
        parsed.recipientQuery
      )
    : null;

  const context = {
    recipient,
    amount: parsed.amount ?? null,
    sendCurrency:
      parsed.currency ||
      event.defaultCurrency ||
      "GBP",
    fundingMethod: null,
    quote: null,
    transfer: null,
    paymentMethod: null,
    phase: null,
  };

  if (!recipient) {
    return {
      ...append(
        state,
        message(
          "bot",
          recipients.length
            ? chatText("chat.recipient.choose")
            : chatText("chat.recipient.first")
        )
      ),
      flow: CHAT_FLOW.SEND_MONEY,
      step: recipients.length
        ? CHAT_STEP.CHOOSING_RECIPIENT
        : CHAT_STEP.CREATING_RECIPIENT,
      context,
      quickReplies: recipients.length
        ? recipientReplies(recipients)
        : [],
      effects: [],
      activeCard: recipients.length
        ? null
        : {
            type: "recipient_form",
          },
    };
  }

  if (!context.amount) {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.amount.prompt", {
          currency: context.sendCurrency,
          recipient: recipientLabel(recipient),
        })
        )
      ),
      flow: CHAT_FLOW.SEND_MONEY,
      step: CHAT_STEP.ENTERING_AMOUNT,
      context,
      quickReplies: [],
      effects: [],
      activeCard: null,
    };
  }

  const effect = quoteEffect(context);

  return {
    ...append(
      state,
      message(
        "bot",
        chatText("chat.quote.getting", {
          amount: moneyLabel(context.sendCurrency, context.amount),
        })
      )
    ),
    flow: CHAT_FLOW.SEND_MONEY,
    step: CHAT_STEP.GETTING_QUOTE,
    context: {
      ...context,
      phase: "quoting",
    },
    quickReplies: [],
    effects: effect ? [effect] : [],
    activeCard: null,
  };
}

function handleIdle(state, event, parsed) {
  switch (parsed.intent) {
    case CHAT_INTENT.SEND_MONEY:
      return startSendFlow(
        state,
        event,
        parsed
      );

    case CHAT_INTENT.ACTIVITY:
      return navigationResponse(
        state,
        chatText("chat.nav.activity"),
        CUSTOMER_ROUTES.activity
      );

    case CHAT_INTENT.RECIPIENTS:
      return navigationResponse(
        state,
        chatText("chat.nav.recipients"),
        CUSTOMER_ROUTES.recipients
      );

    case CHAT_INTENT.RATES:
      return navigationResponse(
        state,
        chatText("chat.nav.rates"),
        CUSTOMER_ROUTES.rates
      );

    case CHAT_INTENT.PROFILE:
      return navigationResponse(
        state,
        chatText("chat.nav.profile"),
        CUSTOMER_ROUTES.profile
      );

    case CHAT_INTENT.SECURITY:
      return navigationResponse(
        state,
        chatText("chat.nav.security"),
        CUSTOMER_ROUTES.security
      );

    case CHAT_INTENT.KYC:
      return navigationResponse(
        state,
        chatText("chat.nav.kyc"),
        CUSTOMER_ROUTES.kyc
      );

    case CHAT_INTENT.SUPPORT:
      return navigationResponse(
        state,
        chatText("chat.nav.support"),
        CUSTOMER_ROUTES.support
      );

    case CHAT_INTENT.NOTIFICATIONS:
      return navigationResponse(
        state,
        chatText("chat.nav.notifications"),
        CUSTOMER_ROUTES.notifications
      );

    case CHAT_INTENT.HELP:
      return resetToHome(
        state,
        chatText("chat.help.menu")
      );

    default:
      return {
        // A model reply already answered; the generic menu would repeat it.
        ...(event.reply?.content
          ? state
          : append(
              state,
              message(
                "bot",
                chatText("chat.help.unknown")
              )
            )),
        quickReplies: homeReplies(),
        effects: [],
      };
  }
}

function handleRecipientStep(state, event) {
  if (
    event.text === "__new_recipient__"
  ) {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.recipient.add_new")
        )
      ),
      step: CHAT_STEP.CREATING_RECIPIENT,
      quickReplies: [],
      effects: [],
      activeCard: {
        type: "recipient_form",
      },
    };
  }

  const recipient = findRecipient(
    event.recipients ?? [],
    event.text
  );

  if (!recipient) {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.recipient.not_found")
        )
      ),
      quickReplies: recipientReplies(
        event.recipients ?? []
      ),
      effects: [],
    };
  }

  const context = {
    ...state.context,
    recipient,
    amount: null,
    quote: null,
    transfer: null,
    paymentMethod: null,
    fundingMethod: null,
    phase: null,
  };

  return {
    ...append(
      state,
      message(
        "bot",
        chatText("chat.amount.prompt", {
          currency: context.sendCurrency,
          recipient: recipientLabel(recipient),
        })
      )
    ),
    step: CHAT_STEP.ENTERING_AMOUNT,
    context,
    quickReplies: [],
    effects: [],
    activeCard: null,
  };
}

function handleAmountStep(state, event) {
  const parsed =
    parsePositiveAmount(event.text);

  if (!parsed) {
    const currency =
      state.context.sendCurrency ||
      event.defaultCurrency ||
      "GBP";

    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.amount.invalid", { currency })
        )
      ),
      effects: [],
    };
  }

  const context = {
    ...state.context,
    amount: parsed.amount,
    sendCurrency:
      parsed.currency ||
      state.context.sendCurrency ||
      event.defaultCurrency ||
      "GBP",
    fundingMethod: null,
    quote: null,
    transfer: null,
    paymentMethod: null,
    phase: "quoting",
  };

  const effect = quoteEffect(context);

  if (!effect) {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.recipient.details_missing")
        )
      ),
      effects: [],
    };
  }

  return {
    ...append(
      state,
      message(
        "bot",
        chatText("chat.amount.accepted", {
          amount: moneyLabel(context.sendCurrency, context.amount),
        })
      )
    ),
    step: CHAT_STEP.GETTING_QUOTE,
    context,
    quickReplies: [],
    effects: [effect],
    activeCard: null,
  };
}

function handleFundingStep(state, event) {
  const value = normalizeChatInput(
    event.text
  )
    .trim()
    .toLowerCase();

  const fundingMethod =
    value === "card" ||
    value === "card payment"
      ? "card"
      : value === "bank" ||
          value === "bank transfer"
        ? "bank"
        : null;

  if (!fundingMethod) {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.funding.choose")
        )
      ),
      quickReplies: fundingReplies(),
      effects: [],
    };
  }

  const context = {
    ...state.context,
    fundingMethod,
    phase: "review",
  };

  return {
    ...append(
      state,
      message(
        "bot",
        chatText("chat.review.check"),
        {
          kind: "transfer_draft",
          transfer: context,
          quote: context.quote,
        }
      )
    ),
    step: CHAT_STEP.REVIEWING_DRAFT,
    context,
    quickReplies: [
      {
        id: "continue",
        label: chatText("chat.reply.continue"),
        value: "continue",
      },
      {
        id: "change-amount",
        label: chatText("chat.reply.change_amount"),
        value: "change amount",
      },
      {
        id: "cancel",
        label: chatText("chat.reply.cancel"),
        value: "cancel",
      },
    ],
    effects: [],
    activeCard: null,
  };
}

function handleReviewStep(state, event) {
  const value = normalizeChatInput(
    event.text
  )
    .trim()
    .toLowerCase();

  if (
    value === "change amount" ||
    value === "edit amount"
  ) {
    const currency =
      state.context.sendCurrency ||
      event.defaultCurrency ||
      "GBP";

    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.amount.change", { currency })
        )
      ),
      step: CHAT_STEP.ENTERING_AMOUNT,
      context: {
        ...state.context,
        amount: null,
        quote: null,
        transfer: null,
        paymentMethod: null,
        fundingMethod: null,
        phase: null,
      },
      quickReplies: [],
      effects: [],
      activeCard: null,
    };
  }

  if (value !== "continue") {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.review.options")
        )
      ),
      effects: [],
    };
  }

  /*
   * Continue is no longer a fake terminal state.
   *
   * It now triggers CREATE_TRANSFER.
   */
  return {
    ...append(
      state,
      message(
        "bot",
        chatText("chat.transfer.preparing")
      )
    ),
    step: CHAT_STEP.CREATING_TRANSFER,
    context: {
      ...state.context,
      phase: "creating_transfer",
    },
    quickReplies: [],
    effects: [
      {
        type: CHAT_EFFECT.CREATE_TRANSFER,
      },
    ],
    activeCard: null,
  };
}

const KNOWN_INTENTS = new Set(
  Object.values(CHAT_INTENT)
);

/*
 * The server's model output, in the shape parseTopLevelIntent returns.
 * The flow re-validates everything: recipient lookup, quote, review, PIN.
 * A currency the customer cannot send in is most likely what the recipient
 * should get ("5000 naira"), so it and its amount are dropped rather than
 * read as a send amount.
 */
export function fromModelIntent(intent = {}) {
  const currency = intent.currency ?? null;
  const sendable = !currency || SEND_CURRENCIES.includes(currency);

  return {
    intent: KNOWN_INTENTS.has(intent.intent)
      ? intent.intent
      : CHAT_INTENT.UNKNOWN,
    amount: sendable ? intent.amount ?? null : null,
    currency: sendable ? currency : null,
    recipientQuery:
      intent.recipient_id ||
      intent.recipient_name ||
      null,
  };
}

function updateMessage(state, id, changes) {
  return {
    ...state,
    messages: state.messages.map((item) =>
      item.id === id
        ? { ...item, ...changes }
        : item
    ),
  };
}

/*
 * Delivery state of a typed message, and history loaded from the server.
 */
function messageLifecycle(state, event) {
  switch (event.type) {
    case "USER_MESSAGE_SENDING":
      return append(
        state,
        message("user", String(event.text).trim(), {
          id: event.localId,
          status: "sending",
          persisted: true,
        })
      );

    case "USER_MESSAGE_SENT":
      return updateMessage(state, event.localId, {
        status: "sent",
        serverId: event.serverId,
      });

    case "USER_MESSAGE_FAILED":
      return updateMessage(state, event.localId, {
        status: "failed",
        errorText: event.errorText,
        retryable: event.retryable !== false,
      });

    case "USER_MESSAGE_RETRY":
      return updateMessage(state, event.localId, {
        status: "sending",
        errorText: null,
      });

    case "USER_MESSAGE_REJECTED":
      return append(
        {
          ...state,
          messages: state.messages.filter(
            (item) => item.id !== event.localId
          ),
        },
        message("bot", event.errorText, {
          persisted: true,
        })
      );

    case "RESET":
      return createInitialConversation({
        firstName: event.firstName,
      });

    case "LOAD_HISTORY": {
      const initial = createInitialConversation({
        firstName: event.firstName,
      });

      return {
        ...initial,
        messages: event.messages.length
          ? event.messages
          : initial.messages,
        historyHasMore: Boolean(event.hasMore),
      };
    }

    case "PREPEND_HISTORY":
      return {
        ...state,
        messages: [
          ...event.messages,
          ...state.messages,
        ],
        historyHasMore: Boolean(event.hasMore),
      };

    default:
      return null;
  }
}

export function conversationReducer(
  state,
  event
) {
  if (event.type === "CLEAR_EFFECTS") {
    return {
      ...state,
      effects: [],
    };
  }

  /*
   * Recipient was really created by the API.
   */
  if (event.type === "RECIPIENT_CREATED") {
    const recipient = event.recipient;

    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.recipient.added", {
            recipient: recipientLabel(recipient),
          })
        ),
        message(
          "bot",
          chatText("chat.amount.prompt", {
            currency:
              state.context.sendCurrency || event.defaultCurrency || "GBP",
            recipient: recipientLabel(recipient),
          })
        )
      ),
      flow: CHAT_FLOW.SEND_MONEY,
      step: CHAT_STEP.ENTERING_AMOUNT,
      context: {
        ...state.context,
        recipient,
        amount: null,
        quote: null,
        fundingMethod: null,
        paymentMethod: null,
        transfer: null,
        phase: null,
      },
      quickReplies: [],
      effects: [],
      activeCard: null,
    };
  }

  if (event.type === "RECIPIENT_CREATE_CANCELLED") {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.recipient.create_cancelled")
        )
      ),
      step: CHAT_STEP.CHOOSING_RECIPIENT,
      quickReplies: recipientReplies(
        event.recipients ?? []
      ),
      effects: [],
      activeCard: null,
    };
  }

  if (event.type === "QUOTE_PREVIEWED") {
    const quote = event.quote;

    return {
      ...state,
      step: CHAT_STEP.CHOOSING_FUNDING,
      context: {
        ...state.context,
        quote,
        phase: "choosing_funding",
      },
      messages: [
        ...state.messages,
        message(
          "bot",
          chatText("chat.quote.receive", {
            recipient: recipientLabel(state.context.recipient),
            amount: moneyLabel(
              quote.receive_currency,
              quote.receive_amount
            ),
          }),
          {
            kind: "quote",
            quote,
            transfer: state.context,
          }
        ),
        message(
          "bot",
          chatText("chat.funding.prompt")
        ),
      ],
      quickReplies: fundingReplies(),
      effects: [],
    };
  }

  if (event.type === "QUOTE_FAILED") {
    return {
      ...state,
      step: CHAT_STEP.ENTERING_AMOUNT,
      context: {
        ...state.context,
        quote: null,
        phase: null,
      },
      messages: [
        ...state.messages,
        message(
          "bot",
          chatText("chat.quote.failed")
        ),
      ],
      quickReplies: [
        {
          id: "cancel",
          label: chatText("chat.reply.cancel"),
          value: "cancel",
        },
      ],
      effects: [],
    };
  }

  /*
   * Real transfer has now been created.
   */
  if (event.type === "TRANSFER_CREATED") {
    const transfer = event.transfer;

    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.transfer.ready"),
          {
            kind: "transfer_ready",
            transfer: {
              ...state.context,
              transfer,
            },
            quote:
              transfer?.quote ||
              state.context.quote,
          }
        )
      ),
      step: CHAT_STEP.AWAITING_PIN,
      context: {
        ...state.context,
        transfer,
        paymentMethod:
          event.paymentMethod ||
          state.context.paymentMethod,
        serverQuote:
          transfer?.quote || null,
        phase: "awaiting_pin",
      },
      quickReplies: [
        {
          id: "cancel",
          label: chatText("chat.reply.cancel"),
          value: "cancel",
        },
      ],
      effects: [],
      activeCard: {
        type: "secure_pin",
      },
    };
  }

  if (event.type === "TRANSFER_CREATE_FAILED") {
    return {
      ...append(
        state,
        message(
          "bot",
          event.message ||
            chatText("chat.transfer.create_failed")
        )
      ),
      step: CHAT_STEP.REVIEWING_DRAFT,
      context: {
        ...state.context,
        transfer: null,
        phase: "review",
      },
      quickReplies: [
        {
          id: "retry",
          label: chatText("chat.reply.retry"),
          value: "continue",
        },
        {
          id: "change-amount",
          label: chatText("chat.reply.change_amount"),
          value: "change amount",
        },
        {
          id: "cancel",
          label: chatText("chat.reply.cancel"),
          value: "cancel",
        },
      ],
      effects: [],
      activeCard: null,
    };
  }

  if (event.type === "TRANSFER_CONFIRMING") {
    return {
      ...state,
      step: CHAT_STEP.CONFIRMING_TRANSFER,
      context: {
        ...state.context,
        phase: "confirming",
      },
      quickReplies: [],
    };
  }

  if (event.type === "BANK_TRANSFER_CONFIRMED") {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.transfer.bank_confirmed"),
          {
            kind: "bank_funding",
            transferId:
              state.context.transfer?.id,
          }
        )
      ),
      step: CHAT_STEP.AWAITING_BANK_FUNDING,
      context: {
        ...state.context,
        phase: "awaiting_bank_funding",
      },
      quickReplies: [
        {
          id: "home",
          label: chatText("chat.reply.main_menu"),
          value: "home",
        },
      ],
      effects: [],
      activeCard: null,
    };
  }

  if (event.type === "CARD_TRANSFER_PREPARED") {
    return {
      ...append(
        state,
        message(
          "bot",
          chatText("chat.transfer.card_ready"),
          {
            kind: "card_funding",
            transferId:
              state.context.transfer?.id,
          }
        )
      ),
      step: CHAT_STEP.AWAITING_CARD_FUNDING,
      context: {
        ...state.context,
        phase: "awaiting_card_funding",
      },
      quickReplies: [
        {
          id: "home",
          label: chatText("chat.reply.main_menu"),
          value: "home",
        },
      ],
      effects: [],
      activeCard: null,
    };
  }

  if (event.type === "TRANSFER_CONFIRM_FAILED") {
    return {
      ...append(
        state,
        message(
          "bot",
          event.message ||
            chatText("chat.transfer.confirm_failed")
        )
      ),
      step: CHAT_STEP.AWAITING_PIN,
      context: {
        ...state.context,
        phase: "awaiting_pin",
      },
      quickReplies: [
        {
          id: "cancel",
          label: chatText("chat.reply.cancel"),
          value: "cancel",
        },
      ],
      effects: [],
      activeCard: {
        type: "secure_pin",
      },
    };
  }

  const lifecycle = messageLifecycle(state, event);

  if (lifecycle) {
    return lifecycle;
  }

  if (
    event.type !== "USER_MESSAGE" &&
    event.type !== "MODEL_INTENT"
  ) {
    return state;
  }

  const text = normalizeChatInput(
    event.text
  );

  if (!text) {
    return state;
  }

  const fromModel =
    event.type === "MODEL_INTENT";

  let next =
    fromModel || event.alreadyShown
      ? state
      : append(
          state,
          message("user", text)
        );

  if (fromModel && event.reply?.content) {
    next = append(
      next,
      message("bot", event.reply.content, {
        serverId: event.reply.id,
        persisted: true,
      })
    );
  }

  if (text === "__open_kyc__") {
    return navigationResponse(
      next,
      chatText("chat.kyc.open"),
      CUSTOMER_ROUTES.kyc
    );
  }

  const parsed =
    fromModel && inputPolicy(state.step).model
      ? fromModelIntent(event.intent)
      : parseTopLevelIntent(text);

  if (
    parsed.intent === CHAT_INTENT.CANCEL
  ) {
    return resetToHome(
      next,
      chatText("chat.home.cancelled")
    );
  }

  if (
    parsed.intent === CHAT_INTENT.HOME
  ) {
    return resetToHome(next);
  }

  if (
    parsed.intent === CHAT_INTENT.BACK &&
    state.step !== CHAT_STEP.IDLE
  ) {
    if (
      state.step ===
      CHAT_STEP.ENTERING_AMOUNT
    ) {
      return {
        ...append(
          next,
          message(
            "bot",
            chatText("chat.recipient.choose_again")
          )
        ),
        step:
          CHAT_STEP.CHOOSING_RECIPIENT,
        context: {
          ...state.context,
          recipient: null,
          amount: null,
          quote: null,
          fundingMethod: null,
          transfer: null,
        },
        quickReplies: recipientReplies(
          event.recipients ?? []
        ),
        effects: [],
        activeCard: null,
      };
    }

    if (
      state.step ===
        CHAT_STEP.CHOOSING_FUNDING ||
      state.step ===
        CHAT_STEP.REVIEWING_DRAFT
    ) {
      const currency =
        state.context.sendCurrency ||
        event.defaultCurrency ||
        "GBP";

      return {
        ...append(
          next,
          message(
            "bot",
            chatText("chat.amount.prompt", {
          currency: currency,
          recipient: recipientLabel(state.context.recipient),
        })
          )
        ),
        step: CHAT_STEP.ENTERING_AMOUNT,
        context: {
          ...state.context,
          amount: null,
          quote: null,
          fundingMethod: null,
          transfer: null,
        },
        quickReplies: [],
        effects: [],
        activeCard: null,
      };
    }
  }

  const policy = inputPolicy(state.step);
  const stepEvent = { ...event, text };

  switch (policy.text) {
    case INPUT_TEXT.RECIPIENT:
      return handleRecipientStep(next, stepEvent);

    case INPUT_TEXT.AMOUNT:
      return handleAmountStep(next, stepEvent);

    case INPUT_TEXT.FUNDING_METHOD:
      return handleFundingStep(next, stepEvent);

    case INPUT_TEXT.REVIEW_CHOICE:
      return handleReviewStep(next, stepEvent);

    // A card owns this step; typed text cannot advance it.
    case INPUT_TEXT.CANCEL_ONLY:
      return {
        ...append(next, message("bot", chatText(policy.hint))),
        effects: [],
      };

    case INPUT_TEXT.INTENT:
    default:
      return handleIdle(next, event, parsed);
  }
}